import { Link, useNavigate, useLocation } from "react-router-dom";
import { LogOut, LayoutDashboard, Sparkles, ExternalLink } from "lucide-react";
import { useAuth } from "@/lib/auth";

const AdminLayout = ({ children, title, action }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const NavLink = ({ to, label, testid }) => (
    <Link
      to={to}
      data-testid={testid}
      className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
        location.pathname === to
          ? "text-white bg-white/[0.06]"
          : "text-slate-400 hover:text-white"
      }`}
    >
      {label}
    </Link>
  );

  return (
    <div className="relative min-h-screen bg-[#060a1a] text-slate-200" data-testid="admin-layout">
      <div className="absolute inset-0 grid-bg-fine opacity-30 pointer-events-none" />

      {/* Top bar */}
      <header className="relative z-10 border-b border-white/[0.06] bg-[#060a1a]/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <Link to="/admin" className="flex items-center gap-2.5 group">
            <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 grid place-items-center shadow-[0_0_22px_rgba(59,130,246,0.4)]">
              <Sparkles className="w-4 h-4 text-white" strokeWidth={2} />
            </span>
            <div className="leading-tight">
              <div className="text-white font-medium text-sm" style={{ fontFamily: "Outfit" }}>
                Joneten · Admin
              </div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-blue-400/80 font-mono-display">
                content console
              </div>
            </div>
          </Link>

          <nav className="hidden sm:flex items-center gap-1">
            <NavLink to="/admin" label="Articles" testid="admin-nav-articles" />
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="admin-nav-public"
              className="px-3 py-1.5 rounded-lg text-sm text-slate-400 hover:text-white transition-colors inline-flex items-center gap-1"
            >
              View site
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </nav>

          <div className="flex items-center gap-3">
            {user && (
              <div className="hidden sm:flex items-center gap-2.5 px-2.5 py-1.5 rounded-full glass">
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="w-6 h-6 rounded-full" />
                ) : (
                  <div className="w-6 h-6 rounded-full bg-blue-500/30 grid place-items-center text-[10px] font-medium text-white">
                    {user.name?.[0] || "A"}
                  </div>
                )}
                <span className="text-xs text-slate-300 font-mono-display max-w-[160px] truncate">
                  {user.email}
                </span>
              </div>
            )}
            <button
              onClick={handleLogout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-white/10 text-sm text-slate-300 hover:text-white hover:border-red-400/40 hover:bg-red-500/[0.05] transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" />
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Page header */}
      <div className="relative z-10 border-b border-white/[0.04]">
        <div className="max-w-7xl mx-auto px-6 py-8 flex items-end justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-blue-400 font-mono-display">
              <LayoutDashboard className="w-3.5 h-3.5" strokeWidth={1.5} />
              /// {title}
            </div>
          </div>
          {action}
        </div>
      </div>

      <main className="relative z-10 max-w-7xl mx-auto px-6 py-10">{children}</main>
    </div>
  );
};

export default AdminLayout;
